import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { apiFetch } from '../lib/api.js'
import { TwemojiImg } from '../lib/twemoji.jsx'
import { GAME_DEFAULTS } from '../lib/constants.js'
import { getSession } from '../lib/session.js'
import { pageBg as podiumBg } from '../lib/images'

// ── Grille joueurs ────────────────────────────────────────────
function PlayerGrid({ players, myId }) {
  return (
    <div className="grid grid-cols-4 gap-x-2 gap-y-3">
      {players.map(p => (
        <div key={p.id} className="flex flex-col items-center gap-1">
          <TwemojiImg emoji={p.type_vehicule} size={48} />
          <span className={`text-xs font-medium text-center leading-tight break-all ${p.id === myId ? 'text-white' : 'text-white/75'}`}>
            {p.pseudo}
          </span>
          {p.id === myId && (
            <span className="text-[10px] text-white/45 -mt-0.5">moi</span>
          )}
        </div>
      ))}
    </div>
  )
}

// ── Indicateur d'attente animé ────────────────────────────────
function WaitingDots() {
  return (
    <div className="flex gap-1.5 justify-center">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="w-2.5 h-2.5 bg-white/60 rounded-full animate-bounce"
          style={{ animationDelay: `${i * 0.15}s` }}
        />
      ))}
    </div>
  )
}

// ── Contenu des cartes d'onboarding ──────────────────────────
const ONBOARDING_CARDS = [
  {
    emoji: '🚥',
    title: 'Comment avancer',
    body: (
      <ul className="space-y-1.5">
        <li className="flex gap-2"><span>✅</span><span>Bonne réponse → tu avances sur le circuit</span></li>
        <li className="flex gap-2"><span>❌</span><span>Mauvaise réponse → tu restes sur place (question remise dans le pool)</span></li>
        <li className="flex gap-2"><span>⏱</span><span>Timer expiré → compté comme une erreur</span></li>
      </ul>
    ),
  },
  {
    emoji: '🔥',
    title: 'Vitesse adaptative',
    body: (
      <div className="space-y-2.5">
        <div className="flex items-start gap-3">
          <span className="text-blue-400 font-bold shrink-0">V1</span>
          <span>+1 case — au départ ou après une erreur</span>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-orange-400 font-bold shrink-0">V2</span>
          <span>+2 cases — après <strong className="text-white">2 bonnes réponses</strong> d'affilée</span>
        </div>
        <div className="flex items-start gap-3">
          <span className="text-red-400 font-bold shrink-0">V3</span>
          <span>+3 cases — après <strong className="text-white">5 bonnes réponses</strong> d'affilée</span>
        </div>
        <p className="text-white text-xs">Une seule erreur = retour immédiat à V1.</p>
      </div>
    ),
  },
  {
    emoji: '🎁',
    title: 'Bonus',
    body: (
      <ul className="space-y-1.5">
        <li className="flex gap-2"><span>⭐</span><span>Case bonus → pioche 1 bonus aléatoire (si inventaire vide)</span></li>
        <li className="flex gap-2"><span>🏁</span><span>Passage départ/arrivée → bonus offert</span></li>
        <li className="flex gap-2"><span>🎒</span><span>Max 1 bonus à la fois — clique pour l'utiliser à tout moment</span></li>
        <li className="flex gap-2 text-amber-300"><span>⚠️</span><span>Inventaire plein = nouveau bonus perdu</span></li>
      </ul>
    ),
  },
  {
    emoji: '🍌🐚💣🪝',
    title: 'Bonus offensifs',
    body: (
      <div className="space-y-2">
        <div className="flex gap-2.5">
          <span className="shrink-0">🍌</span>
          <span><strong className="text-white">Banane</strong> — piège sur ta case (-1 case au prochain qui s'y arrête)</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">🐚</span>
          <span><strong className="text-white">Coquille</strong> — timer ÷2 pour le joueur juste devant toi</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">💣</span>
          <span><strong className="text-white">Bombe</strong> — -3 cases pour le joueur devant (et ses voisins de case)</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">🪝</span>
          <span><strong className="text-white">Crochet</strong> — -3 cases au 1er, +1 case pour toi</span>
        </div>
      </div>
    ),
  },
  {
    emoji: '🚀🛡⭐❄️',
    title: 'Bonus spéciaux',
    body: (
      <div className="space-y-2">
        <div className="flex gap-2.5">
          <span className="shrink-0">🚀</span>
          <span><strong className="text-white">Turbo</strong> — +2 cases immédiats</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">🛡</span>
          <span><strong className="text-white">Bouclier</strong> — absorbe la prochaine attaque reçue (30 s)</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">⭐</span>
          <span><strong className="text-white">Étoile</strong> — invincible à toutes attaques pendant 60 s</span>
        </div>
        <div className="flex gap-2.5">
          <span className="shrink-0">❄️</span>
          <span><strong className="text-white">Gel</strong> — fige tous les autres joueurs pendant 12 s</span>
        </div>
      </div>
    ),
  },
  {
    emoji: '🏁',
    title: 'Fin de partie',
    body: (
      <ul className="space-y-1.5">
        <li className="flex gap-2"><span>🥇</span><span>Sois le premier à terminer tous les tours</span></li>
        <li className="flex gap-2"><span>🏆</span><span>Podium animé + classement complet</span></li>
        <li className="flex gap-2"><span>📊</span><span>Récap personnalisé : points forts &amp; points à retravailler</span></li>
      </ul>
    ),
  },
]

// ── Carousel d'onboarding ─────────────────────────────────────
function OnboardingCarousel() {
  const [current, setCurrent] = useState(0)
  const touchStartX = useRef(null)
  const total = ONBOARDING_CARDS.length

  function goPrev() { setCurrent(c => Math.max(0, c - 1)) }
  function goNext() { setCurrent(c => Math.min(total - 1, c + 1)) }

  function handleTouchStart(e) {
    touchStartX.current = e.touches[0].clientX
  }

  function handleTouchEnd(e) {
    if (touchStartX.current === null) return
    const delta = e.changedTouches[0].clientX - touchStartX.current
    if (delta < -40) goNext()
    else if (delta > 40) goPrev()
    touchStartX.current = null
  }

  return (
    <div className="bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl flex flex-col">
      {/* En-tête */}
      <div className="flex items-center justify-between px-4 pt-4 pb-1">
        <p className="text-white/50 text-xs font-semibold uppercase tracking-widest">Règles du jeu</p>
        <span className="text-white/35 text-xs">{current + 1} / {total}</span>
      </div>

      {/* Zone de slide */}
      <div
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className="flex transition-transform duration-300 ease-out"
          style={{ transform: `translateX(-${current * 100}%)` }}
        >
          {ONBOARDING_CARDS.map((card, i) => (
            <div key={i} className="min-w-full px-4 pt-3 pb-2">
              <div className="text-2xl mb-2 leading-none">{card.emoji}</div>
              <h3 className="text-white font-bold text-sm mb-2">{card.title}</h3>
              <div className="text-white text-sm leading-relaxed">{card.body}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation : flèches + points */}
      <div className="flex items-center justify-between px-3 pt-2 pb-3">
        <button
          onClick={goPrev}
          disabled={current === 0}
          aria-label="Carte précédente"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-lg text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition"
        >
          ‹
        </button>

        <div className="flex gap-1.5 items-center">
          {ONBOARDING_CARDS.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              aria-label={`Carte ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-200 ${
                i === current ? 'bg-white w-4' : 'bg-white/30 w-1.5'
              }`}
            />
          ))}
        </div>

        <button
          onClick={goNext}
          disabled={current === total - 1}
          aria-label="Carte suivante"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-lg text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-20 disabled:cursor-not-allowed transition"
        >
          ›
        </button>
      </div>
    </div>
  )
}

// ── Page Lobby ────────────────────────────────────────────────
export default function LobbyPage() {
  const { code } = useParams()
  const navigate  = useNavigate()

  const [kart, setKart]       = useState(null)
  const [game, setGame]       = useState(null)
  const [players, setPlayers] = useState([])
  const [error, setError]     = useState('')
  const pollingRef = useRef(null)

  const session = getSession(code)

  useEffect(() => {
    if (!session) navigate(`/join/${code}`, { replace: true })
  }, [])

  useEffect(() => {
    if (!session) return
    apiFetch(`/api/karts/${code}/me`, {
      headers: { 'x-session-token': session.token },
    })
      .then(({ kart, game }) => { setKart(kart); setGame(game) })
      .catch(() => navigate(`/join/${code}`, { replace: true }))
  }, [code])

  useEffect(() => {
    if (!session) return

    async function poll() {
      try {
        const gameData = await apiFetch(`/api/games/${code}?include=players`)
        setGame(gameData)
        setPlayers(gameData.players ?? [])
        if (gameData.statut === 'playing') {
          clearInterval(pollingRef.current)
          navigate(`/game/${code}`, { replace: true })
        }
      } catch { /* silencieux */ }
    }

    poll()
    pollingRef.current = setInterval(poll, GAME_DEFAULTS.POLLING_MS)
    return () => clearInterval(pollingRef.current)
  }, [code])

  if (!session) return null

  const deckTitle  = game?.decks?.titre ?? '…'
  const maxJoueurs = game?.config?.max_joueurs ?? GAME_DEFAULTS.MAX_PLAYERS

  const bgStyle = { backgroundImage: `url(${podiumBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }

  return (
    <div className="h-[100dvh] overflow-y-auto" style={bgStyle}>

      <div className="px-4 pb-4">
        {/* Header */}
        <div className="flex justify-center pt-4 pb-2">
          <img src="/android-chrome-192x192.png" alt="KartQuiz" className="w-14 h-14 rounded-full object-cover" />
        </div>
        {/*
          Mobile (flex-col) : kart → attente → carousel → joueurs
          Desktop (md+, grid 2 col) : col gauche = kart/attente/joueurs, col droite = carousel
          Le carousel est 3e dans le DOM → 3e sur mobile, repositionné col-2 sur desktop
        */}
        <div className="w-full max-w-sm mx-auto md:max-w-3xl">
          <div className="flex flex-col gap-4 md:grid md:grid-cols-[1fr_300px] md:gap-5 md:items-start">

            {/* Mon kart — col 1 / row 1 */}
            {kart && (
              <div className="md:col-start-1 md:row-start-1 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 flex flex-col items-center gap-2">
                <TwemojiImg emoji={kart.type_vehicule} size={72} />
                <div className="text-white font-semibold text-xl">{kart.pseudo}</div>
              </div>
            )}

            {/* Attente — col 1 / row 2 */}
            <div className="md:col-start-1 md:row-start-2 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 text-center space-y-3">
              <WaitingDots />
              <p className="text-white font-medium">
                {game?.statut === 'paused' ? 'Partie en pause…' : 'En attente du démarrage…'}
              </p>
              <p className="text-white/60 text-sm">{deckTitle}</p>
              {error && <p className="text-red-300 text-sm">{error}</p>}
            </div>

            {/* Onboarding carousel — col 2 / rows 1-3 sur desktop, 3e sur mobile */}
            <div className="md:col-start-2 md:row-start-1 md:row-span-3">
              <OnboardingCarousel />
            </div>

            {/* Joueurs connectés — col 1 / row 3 */}
            <div className="md:col-start-1 md:row-start-3 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-4">
              <div className="flex justify-between items-center mb-3">
                <p className="text-white/50 text-xs font-semibold uppercase tracking-widest">Joueurs connectés</p>
                <span className="text-white/35 text-xs">{players.length}/{maxJoueurs}</span>
              </div>
              {players.length === 0 ? (
                <p className="text-white/40 text-sm text-center py-2">Personne encore…</p>
              ) : (
                <PlayerGrid players={players} myId={kart?.id} />
              )}
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}
